import { useEffect, useState } from 'react'
import { Button, Input, Label, SearchField, TextField, ToggleButton } from '@heroui/react'
import { api } from '../lib/api'
import { searchable, shortPhone } from '../lib/customer'
import { explain } from '../lib/staffErrors'
import type { KnownCustomer, Ticket } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import { CounterPanel } from './counterDialogs'

/** Wait this long after typing before searching. */
const SEARCH_AFTER_MS = 300

/** Add or edit who the customer is: pick a known customer, or type name and mobile. */
export function CustomerPanel({
  branchId,
  ticket,
  bookedName,
  onClose,
}: {
  branchId: string
  ticket: Ticket
  bookedName?: string
  onClose: () => void
}) {
  const { lang, say } = useStaffText()
  const [q, setQ] = useState('')
  const [found, setFound] = useState<KnownCustomer[] | null>(null)
  const [name, setName] = useState(ticket.customerName ?? bookedName ?? '')
  const [phone, setPhone] = useState(ticket.customerPhone ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!searchable(q)) {
      setFound(null)
      return
    }
    let live = true
    const timer = window.setTimeout(() => {
      api
        .searchCustomers(branchId, q.trim())
        .then((r) => live && setFound(r.customers))
        .catch((err) => live && setError(explain(err, lang, 'common.unreachable')))
    }, SEARCH_AFTER_MS)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [q, branchId, lang])

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await api.setTicketCustomer(ticket.id, name.trim(), phone.trim())
      onClose()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <CounterPanel
      title={say('counter.customerTitle', { number: ticket.displayNumber })}
      onClose={onClose}
      foot={
        <>
          <Button variant="secondary" size="sm" type="submit" form="counter-customer" isDisabled={saving} isPending={saving}>
            {say('common.save')}
          </Button>
          <Button variant="ghost" size="sm" onPress={onClose}>{say('common.cancel')}</Button>
        </>
      }
    >
      <form
        id="counter-customer"
        className="cw-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (!saving) void save()
        }}
      >
        {error && <p className="cw-inline__error" role="alert">{error}</p>}
        <SearchField className="cw-field" variant="secondary" value={q} onChange={setQ} autoFocus fullWidth>
          <Label>{say('counter.customerFind')}</Label>
          <SearchField.Group>
            <SearchField.SearchIcon />
            <SearchField.Input maxLength={100} placeholder={say('counter.customerFindHint')} />
            <SearchField.ClearButton />
          </SearchField.Group>
        </SearchField>
        {found !== null && (
          <div className="cw-chips" role="group" aria-label={say('counter.customerFind')}>
            {found.length === 0 && <p className="counter-hint">{say('counter.customerNone')}</p>}
            {found.map((c) => (
              <ToggleButton
                key={`${c.name ?? ''}|${c.phone ?? ''}`}
                size="sm"
                isSelected={name === (c.name ?? '') && phone === (c.phone ?? '')}
                onChange={() => {
                  setName(c.name ?? '')
                  setPhone(c.phone ?? '')
                }}
              >
                <span className="cw-found__name">{c.name || '—'}</span>
                <span className="cw-found__sub">{shortPhone(c.phone)}</span>
              </ToggleButton>
            ))}
          </div>
        )}
        <div className="cw-inline__row">
          <TextField className="cw-field cw-inline__grow" value={name} onChange={setName} fullWidth>
            <Label>{say('counter.customerName')}</Label>
            <Input variant="secondary" maxLength={100} autoComplete="off" />
          </TextField>
          <TextField className="cw-field cw-inline__grow" type="tel" value={phone} onChange={setPhone} fullWidth>
            <Label>{say('counter.customerPhone')}</Label>
            <Input variant="secondary" maxLength={24} autoComplete="off" placeholder={say('counter.customerPhoneHint')} />
          </TextField>
        </div>
      </form>
    </CounterPanel>
  )
}
